import {
    createContext,
    useContext,
    ReactNode,
    useMemo,
} from "react";
import { doc, updateDoc } from "firebase/firestore";
import { db } from "../configs/firebase";
import { useAuth, UserAddress } from "../auth/AuthContext";

interface DeliveryAddressContextType {
    addresses: UserAddress[];
    currentAddress?: UserAddress;
    setCurrentAddress: (id: string) => Promise<void>;
    addAddress: (address: UserAddress) => Promise<void>;
}

const DeliveryAddressContext =
    createContext<DeliveryAddressContextType | undefined>(undefined);

export const DeliveryAddressProvider = ({
    children,
}: {
    children: ReactNode;
}) => {
    const { user, userSettings, refreshUserSettings } = useAuth();

    const addresses = userSettings?.addresses ?? [];

    const currentAddress = useMemo(
        () => addresses.find(a => a.isCurrent),
        [addresses]
    );

    const setCurrentAddress = async (id: string) => {
        if (!user) return;

        const updated = addresses.map(addr => ({
            ...addr,
            isCurrent: addr.id === id,
        }));

        await updateDoc(doc(db, "users", user.uid), {
            addresses: updated,
        });

        // 🔥 CRITICAL
        await refreshUserSettings();
    };

    const addAddress = async (address: UserAddress) => {
        if (!user) return;

        const updated = [
            ...addresses.map(a => ({ ...a, isCurrent: false })),
            address,
        ];

        await updateDoc(doc(db, "users", user.uid), {
            addresses: updated,
        });

        // 🔥 CRITICAL
        await refreshUserSettings();
    };

    return (
        <DeliveryAddressContext.Provider
            value={{
                addresses,
                currentAddress,
                setCurrentAddress,
                addAddress,
            }}
        >
            {children}
        </DeliveryAddressContext.Provider>
    );
};

export const useDeliveryAddress = () => {
    const ctx = useContext(DeliveryAddressContext);
    if (!ctx) {
        throw new Error(
            "useDeliveryAddress must be used inside DeliveryAddressProvider"
        );
    }
    return ctx;
};
